import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../../../config/firebase";

interface Props {
  show: boolean;
  userID: string;
  firstName: string;
  onComplete: () => void;
}

type UserRole = "entrepreneur" | "indie_hacker" | "freelancer" | "student";
type UserStage = "just_idea" | "been_thinking" | "already_started";

const ROLES: { value: UserRole; label: string; description: string; icon: string }[] = [
  { value: "entrepreneur", label: "Aspiring Entrepreneur", description: "Looking to launch my first business", icon: "bi-briefcase" },
  { value: "indie_hacker", label: "Indie Hacker", description: "Building products solo or in small teams", icon: "bi-laptop" },
  { value: "freelancer", label: "Freelancer or Agency", description: "Building products for clients or myself", icon: "bi-people" },
  { value: "student", label: "Student", description: "Exploring entrepreneurship and business ideas", icon: "bi-mortarboard" },
];

const STAGES: { value: UserStage; label: string; description: string; icon: string }[] = [
  { value: "just_idea", label: "Just Had an Idea", description: "I have a concept but haven't started yet", icon: "bi-lightbulb" },
  { value: "been_thinking", label: "Been Thinking for Months", description: "I've been researching and planning", icon: "bi-clock-history" },
  { value: "already_started", label: "Already Started Something", description: "I have a prototype, users, or early traction", icon: "bi-rocket" },
];

const PHASES = [
  {
    number: 1,
    title: "Validate",
    description: "24 structured steps to pressure-test your idea with market data",
    icon: "bi-check2-all",
    color: "#3B82F6",
    bg: "#DBEAFE",
  },
  {
    number: 2,
    title: "Build",
    description: "AI autonomously scaffolds your MVP — code, architecture, stories",
    icon: "bi-cpu",
    color: "#8B5CF6",
    bg: "#EDE9FE",
  },
  {
    number: 3,
    title: "Launch",
    description: "Generate logos, landing pages, emails, and social assets instantly",
    icon: "bi-rocket-takeoff",
    color: "#10B981",
    bg: "#D1FAE5",
  },
];

export const OnboardingWizardModal: React.FC<Props> = ({ show, userID, firstName, onComplete }) => {
  const [step, setStep] = useState(1);
  const [role, setRole] = useState<UserRole | null>(null);
  const [stage, setStage] = useState<UserStage | null>(null);
  const [saving, setSaving] = useState(false);

  const totalSteps = 4;

  const handleSkip = async () => {
    await markComplete();
    onComplete();
  };

  const markComplete = async () => {
    setSaving(true);
    try {
      const userRef = doc(db, "users", userID);
      await updateDoc(userRef, {
        onboardingComplete: true,
        ...(role ? { userRole: role } : {}),
        ...(stage ? { userStage: stage } : {}),
      });
    } catch (err) {
      // Non-critical — don't block the user if Firestore write fails
      console.error("Failed to save onboarding data:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    if (step < totalSteps) {
      setStep((s) => s + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep((s) => s - 1);
    }
  };

  const handleFinish = async () => {
    await markComplete();
    onComplete();
  };

  const canProceed = () => {
    if (step === 2) return role !== null;
    if (step === 3) return stage !== null;
    return true;
  };

  return (
    <Modal show={show} onHide={handleSkip} size="lg" centered backdrop="static" keyboard={false}>
      <Modal.Body className="p-0" style={{ borderRadius: "16px", overflow: "hidden" }}>
        {/* Header gradient */}
        <div
          style={{
            background: "linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)",
            padding: "28px 32px 24px",
            position: "relative",
          }}
        >
          {/* Skip button */}
          <button
            className="btn btn-icon btn-sm position-absolute"
            onClick={handleSkip}
            style={{ top: "16px", right: "16px", background: "rgba(255,255,255,0.2)", color: "white" }}
            title="Skip for now"
          >
            <i className="bi bi-x fs-4 text-white"></i>
          </button>

          {/* Brand */}
          <div className="d-flex align-items-center gap-2 mb-3">
            <i className="bi bi-lightning-charge-fill text-white fs-5"></i>
            <span style={{ color: "rgba(255,255,255,0.85)", fontSize: "13px", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}>
              Business Foundry
            </span>
          </div>

          {step === 1 && (
            <>
              <h2 style={{ color: "white", fontWeight: 700, fontSize: "26px", lineHeight: "1.2", marginBottom: "8px" }}>
                {firstName ? `Welcome, ${firstName}!` : "Welcome to Business Foundry!"}
              </h2>
              <p style={{ color: "rgba(255,255,255,0.82)", fontSize: "15px", margin: 0 }}>
                Let's get you set up. It takes about 60 seconds.
              </p>
            </>
          )}
          {step === 2 && (
            <>
              <h2 style={{ color: "white", fontWeight: 700, fontSize: "22px", lineHeight: "1.3", marginBottom: "8px" }}>
                What best describes you?
              </h2>
              <p style={{ color: "rgba(255,255,255,0.82)", fontSize: "14px", margin: 0 }}>
                We'll tailor the experience to your needs.
              </p>
            </>
          )}
          {step === 3 && (
            <>
              <h2 style={{ color: "white", fontWeight: 700, fontSize: "22px", lineHeight: "1.3", marginBottom: "8px" }}>
                Where are you right now?
              </h2>
              <p style={{ color: "rgba(255,255,255,0.82)", fontSize: "14px", margin: 0 }}>
                Understanding your stage helps us show the most relevant next steps.
              </p>
            </>
          )}
          {step === 4 && (
            <>
              <h2 style={{ color: "white", fontWeight: 700, fontSize: "22px", lineHeight: "1.3", marginBottom: "8px" }}>
                Here's how Business Foundry works
              </h2>
              <p style={{ color: "rgba(255,255,255,0.82)", fontSize: "14px", margin: 0 }}>
                Three phases from idea to paying customers.
              </p>
            </>
          )}
        </div>

        {/* Body */}
        <div style={{ padding: "28px 32px", backgroundColor: "#FFFFFF" }}>

          {/* Step 1: Intro cards */}
          {step === 1 && (
            <div>
              <p style={{ color: "#475569", fontSize: "15px", marginBottom: "20px" }}>
                Business Foundry guides you through a proven <strong>24-step validation framework</strong>,
                then autonomously builds your MVP and generates launch assets — all powered by AI.
              </p>
              <div className="row g-3">
                {PHASES.map((phase) => (
                  <div key={phase.number} className="col-4">
                    <div
                      style={{
                        border: "1px solid #E2E8F0",
                        borderRadius: "12px",
                        padding: "16px",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          width: "44px",
                          height: "44px",
                          borderRadius: "10px",
                          backgroundColor: phase.bg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "0 auto 10px",
                        }}
                      >
                        <i className={`bi ${phase.icon}`} style={{ color: phase.color, fontSize: "20px" }}></i>
                      </div>
                      <div style={{ fontWeight: 700, color: "#0F172A", fontSize: "14px" }}>{phase.title}</div>
                      <div style={{ color: "#94A3B8", fontSize: "12px", marginTop: "4px" }}>{phase.description.split(" ").slice(0, 6).join(" ")}…</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Role selection */}
          {step === 2 && (
            <div className="d-flex flex-column gap-3">
              {ROLES.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setRole(r.value)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "14px",
                    padding: "14px 16px",
                    borderRadius: "10px",
                    border: `2px solid ${role === r.value ? "#3B82F6" : "#E2E8F0"}`,
                    backgroundColor: role === r.value ? "#DBEAFE" : "#FFFFFF",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 150ms ease-out",
                  }}
                >
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      minWidth: "40px",
                      borderRadius: "8px",
                      backgroundColor: role === r.value ? "#3B82F6" : "#F1F5F9",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <i className={`bi ${r.icon}`} style={{ color: role === r.value ? "white" : "#64748B", fontSize: "18px" }}></i>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: "#0F172A", fontSize: "14px" }}>{r.label}</div>
                    <div style={{ color: "#64748B", fontSize: "13px", marginTop: "2px" }}>{r.description}</div>
                  </div>
                  {role === r.value && (
                    <i className="bi bi-check-circle-fill ms-auto" style={{ color: "#3B82F6", fontSize: "20px" }}></i>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Step 3: Stage selection */}
          {step === 3 && (
            <div className="d-flex flex-column gap-3">
              {STAGES.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setStage(s.value)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "14px",
                    padding: "16px 16px",
                    borderRadius: "10px",
                    border: `2px solid ${stage === s.value ? "#8B5CF6" : "#E2E8F0"}`,
                    backgroundColor: stage === s.value ? "#EDE9FE" : "#FFFFFF",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 150ms ease-out",
                  }}
                >
                  <div
                    style={{
                      width: "44px",
                      height: "44px",
                      minWidth: "44px",
                      borderRadius: "8px",
                      backgroundColor: stage === s.value ? "#8B5CF6" : "#F1F5F9",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <i className={`bi ${s.icon}`} style={{ color: stage === s.value ? "white" : "#64748B", fontSize: "20px" }}></i>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: "#0F172A", fontSize: "15px" }}>{s.label}</div>
                    <div style={{ color: "#64748B", fontSize: "13px", marginTop: "2px" }}>{s.description}</div>
                  </div>
                  {stage === s.value && (
                    <i className="bi bi-check-circle-fill ms-auto" style={{ color: "#8B5CF6", fontSize: "20px" }}></i>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Step 4: How it works */}
          {step === 4 && (
            <div>
              <div className="d-flex flex-column gap-3 mb-4">
                {PHASES.map((phase, idx) => (
                  <div
                    key={phase.number}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "16px",
                      padding: "16px",
                      borderRadius: "12px",
                      border: "1px solid #E2E8F0",
                      backgroundColor: "#FAFAFA",
                    }}
                  >
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        minWidth: "48px",
                        borderRadius: "10px",
                        backgroundColor: phase.bg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <i className={`bi ${phase.icon}`} style={{ color: phase.color, fontSize: "22px" }}></i>
                    </div>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                        <span
                          style={{
                            backgroundColor: phase.color,
                            color: "white",
                            borderRadius: "50%",
                            width: "20px",
                            height: "20px",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "11px",
                            fontWeight: 700,
                          }}
                        >
                          {phase.number}
                        </span>
                        <span style={{ fontWeight: 700, color: "#0F172A", fontSize: "15px" }}>Phase {phase.number}: {phase.title}</span>
                      </div>
                      <p style={{ color: "#475569", fontSize: "13px", margin: 0 }}>{phase.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div
                style={{
                  backgroundColor: "#F0FDF4",
                  border: "1px solid #BBF7D0",
                  borderRadius: "10px",
                  padding: "14px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <i className="bi bi-stars" style={{ color: "#10B981", fontSize: "20px" }}></i>
                <p style={{ color: "#065F46", fontSize: "13px", margin: 0 }}>
                  <strong>You're in good company.</strong> Business Foundry helps founders validate ideas before they waste months building the wrong thing.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "16px 32px 24px",
            backgroundColor: "#FFFFFF",
            borderTop: "1px solid #E2E8F0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* Step dots */}
          <div style={{ display: "flex", gap: "6px" }}>
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                style={{
                  width: i + 1 === step ? "20px" : "8px",
                  height: "8px",
                  borderRadius: "4px",
                  backgroundColor: i + 1 <= step ? "#3B82F6" : "#E2E8F0",
                  transition: "all 200ms ease-out",
                }}
              />
            ))}
          </div>

          {/* Navigation */}
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            {step > 1 && (
              <button
                className="btn btn-light fw-semibold"
                onClick={handleBack}
                style={{ fontSize: "14px", padding: "8px 18px", borderRadius: "8px" }}
              >
                Back
              </button>
            )}
            {step < totalSteps ? (
              <button
                className="btn btn-primary fw-semibold"
                onClick={handleNext}
                disabled={!canProceed()}
                style={{ fontSize: "14px", padding: "8px 24px", borderRadius: "8px" }}
              >
                Next <i className="bi bi-arrow-right ms-1"></i>
              </button>
            ) : (
              <button
                className="btn btn-primary fw-semibold"
                onClick={handleFinish}
                disabled={saving}
                style={{ fontSize: "14px", padding: "8px 24px", borderRadius: "8px" }}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Saving…
                  </>
                ) : (
                  <>
                    Start Your First Project <i className="bi bi-rocket-takeoff ms-1"></i>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};
